import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useStore } from "react-redux";

import { TranslationMessage } from "@bookingcom/lingojs-core/build/t";
import { t } from "@bookingcom/lingojs-core";

import { StoreState } from "../store";
import useGlobalContext from "../hooks/useGlobalContext";

import { assert } from "../utils/assert";
import { PARAM_LIST_SEPARATOR, RouteName } from "@flights/constants";
import useCreateUrl from "../hooks/useCreateUrl";
import {
  PRE_OPTED_ANCILLARIES_QUERY_PARAMS,
  BRANDED_FARES_QUERY_PARAMS,
  PRE_FETCHED_FARES_QUERY_PARAMS
} from "../constants";
import { getFlightToken } from "../store/flightDetails/selectors";
import { getCartToken } from "../store/cartDetails/selectors";
import { getOrderToken } from "../store/order/selectors";
import { getSearch } from "../store/search/selectors";

const isMobile = Boolean(window.__GLOBAL_CONTEXT__?.userAgent?.isMobile);
const isPaxWithBaggages = !isMobile;

type NavigationScreen = {
  title: TranslationMessage;
  url: (context: RouteContext) => string;
};

type NavigationScreens = {
  [key in RouteName]?: NavigationScreen;
};

type RouteContext = {
  search: StoreState["search"];
  flightToken: string | undefined;
  cartToken: string | undefined;
  orderToken: string | undefined;
};

const screens: NavigationScreens = {
  home: {
    title: t("flights_index_header_compare_book_ease"),
    url: () => "/"
  },

  /**
   * Checkout start is a loading screen that determines what checkout step to start with.
   * It waits for the flight information to be fetched and then based on that information redirects to the correct first step.
   * @see the useAvailableCheckoutSteps() hook which determines the checkout steps to show.
   */
  "checkout-start": {
    title: t("flights_page_title_bp_generic"),
    url: () => {
      throw Error("Unable to build `checkout-start` url. Flight id is not yet known in store.");
    }
  },

  "checkout-ticket-type": {
    title: t("flights_progress_ticket_type"),
    url: (context: RouteContext) => {
      const { flightToken, cartToken } = context;

      assert(flightToken, "Missing flight details for checkout nav");
      assert(cartToken, "Missing cart token");

      return `/checkout/ticket-type/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
    }
  },

  "checkout-fare": {
    title: t("flights_checkout_stepper_choose_fare"),
    url: (context: RouteContext) => {
      const { flightToken, cartToken } = context;

      assert(flightToken, "Missing flight details for checkout nav");
      assert(cartToken, "Missing cart token");

      return `/checkout/fare/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
    }
  },

  "checkout-pax": {
    title: isPaxWithBaggages
      ? t("flights_checkout_stepper_your_details")
      : t("flights_checkout_passenger_details_stepper"),
    url: (context: RouteContext) => {
      const { flightToken, cartToken } = context;

      assert(flightToken, "Missing flight details for checkout nav");
      assert(cartToken, "Missing cart token");

      return `/checkout/pax/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
    }
  },

  "checkout-extras": {
    title: isPaxWithBaggages ? t("flights_checkout_stepper_extras") : t("flights_stepper_baggage"),
    url: (context: RouteContext) => {
      const { flightToken, cartToken } = context;

      assert(flightToken, "Missing flight details for checkout nav");
      assert(cartToken, "Missing cart token");

      return `/checkout/extras/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
    }
  },

  "checkout-seat-selection": {
    title: t("flights_seatmap_dt_header_select", { num_exception: 1 }),
    url: (context: RouteContext) => {
      const { flightToken, cartToken } = context;

      assert(flightToken, "Missing flight details for checkout nav");
      assert(cartToken, "Missing cart token");

      return `/checkout/seats/${flightToken}/${cartToken}`;
    }
  },

  // SIRF = Spanish islands resident fare
  "checkout-sirf": {
    title: t("flights_checkout_review_stepper"),
    url: (context: RouteContext) => {
      const { flightToken, cartToken } = context;

      assert(flightToken, "Missing flight details for checkout nav");
      assert(cartToken, "Missing cart token");

      return `/checkout/sirf/${flightToken}/${cartToken}`;
    }
  },

  "checkout-payment": {
    title: t("flights_checkout_review_stepper"),
    url: (context: RouteContext) => {
      const { flightToken, orderToken } = context;

      assert(flightToken, "Missing flight details for checkout nav");
      assert(orderToken, "Missing orderToken");

      return `/checkout/payment/${flightToken}/${orderToken}`;
    }
  },

  // FLIGHTS_WEB_PAYMENT_COLLECTION_NEW_PAGE
  "checkout-collection": {
    title: t("flights_checkout_review_stepper"),
    url: (context: RouteContext) => {
      const { flightToken, orderToken } = context;

      assert(flightToken, "Missing flight details for checkout nav");
      assert(orderToken, "Missing orderToken");

      return `/checkout/payment/${flightToken}/${orderToken}`;
    }
  }
  // FLIGHTS_WEB_PAYMENT_COLLECTION_NEW_PAGE
};

const getQueryParamsToPreserve = (): URLSearchParams => {
  const paramsToPreserve = pickParamsFromCurrentUrl(
    Object.values({
      ...PRE_OPTED_ANCILLARIES_QUERY_PARAMS,
      ...BRANDED_FARES_QUERY_PARAMS,
      ...PRE_FETCHED_FARES_QUERY_PARAMS
    })
  );
  return new URLSearchParams(paramsToPreserve);
};

const pickParamsFromCurrentUrl = (params: string[]): Record<string, string> => {
  const currentParams = new URLSearchParams(location.search);
  const picked = {};

  params.forEach((param) => {
    if (currentParams.has(param)) {
      // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
      picked[param] = currentParams.get(param);
    }
  });

  return picked;
};

const DEEPLINKS_PROTOCOL = "booking:";

function searchParamsFromState(search: StoreState["search"]) {
  const params = new URLSearchParams();

  const { searchSegments } = search;
  const { from, to, departureDate, returnDate } = searchSegments[0];
  params.set("type", search.type);
  params.set("adults", `${search.adults}`);
  params.set("cabinClass", search.cabinClass);
  params.set("children", search.children.join(PARAM_LIST_SEPARATOR));
  params.set("depart", departureDate || "");
  params.set("sort", search.sort);
  params.set("from", from.map((l) => l.code).join(PARAM_LIST_SEPARATOR));
  params.set("to", to.map((l) => l.code).join(PARAM_LIST_SEPARATOR));
  if (search.type == "ROUNDTRIP") {
    params.set("return", `${returnDate}`);
  }
  return params;
}

/* eslint-disable-next-line flights/no-unassigned-todo-comments */
// TODO: (asamilyak): Remove deep links support once apps go entirely native for all screens
const nativeAppScreens: NavigationScreens = {
  home: {
    title: t("flights_index_header_compare_book_ease"),
    url: function (context: RouteContext) {
      const params = searchParamsFromState(context.search);
      return `${DEEPLINKS_PROTOCOL}//flights?${params}`;
    }
  }
};

export const getScreen = (routeName: RouteName): NavigationScreen => {
  const screen = screens[routeName];
  if (!screen) {
    throw Error(`Invalid screen: ${routeName}`);
  }

  return screen;
};

export const getScreenTitle = (routeName?: RouteName): TranslationMessage | undefined => {
  if (!routeName) return;
  return screens[routeName]?.title;
};

export const getNativeScreen = (routeName: RouteName): NavigationScreen => {
  const screen = nativeAppScreens[routeName];
  if (!screen) {
    throw Error(`Invalid native screen: ${routeName}`);
  }

  return screen;
};

export const useScreenUrl = (routeName: RouteName, options?: { doNotThrowErrorWithoutTriggeringAction: boolean }) => {
  const { createUrl } = useCreateUrl();
  const isNativeScreen = useIsNativeScreen();
  const store = useStore<StoreState>();

  let screen: NavigationScreen | undefined = undefined;

  // getScreen & getNativeScreen can throw, in some cases we don't want the hook to throw immediately.
  if (!options?.doNotThrowErrorWithoutTriggeringAction) {
    screen = isNativeScreen ? getNativeScreen(routeName) : getScreen(routeName);
  }

  // Return a function instead of the direct value, so that we then only throw when we try to navigate.
  const getScreenUrl = useCallback(() => {
    const routeContext = getRouteContext(store.getState());
    const localScreen = screen || (isNativeScreen ? getNativeScreen(routeName) : getScreen(routeName));
    const url = localScreen.url(routeContext);

    return createUrl(url);
  }, [createUrl, isNativeScreen, routeName, screen, store]);

  return { getScreenUrl };
};

export const useScreenNavigation = (routeName: RouteName): (() => void) => {
  const isNativeScreen = useIsNativeScreen();
  const { getScreenUrl } = useScreenUrl(routeName);
  const history = useHistory();

  return useCallback(() => {
    if (isNativeScreen) {
      window.location.href = getScreenUrl();
    } else {
      history.push(getScreenUrl());
    }
  }, [history, getScreenUrl, isNativeScreen]);
};

const getRouteContext = (state: StoreState): RouteContext => {
  const flightToken = getFlightToken(state);
  const cartToken = getCartToken(state);
  const orderToken = getOrderToken(state);
  const search = getSearch(state);

  return { search, flightToken, cartToken, orderToken };
};

const useIsNativeScreen = () => {
  const { userAgent, features } = useGlobalContext();
  return !!userAgent.isWebview && !!features.FLIGHTS_ENABLE_APP_DEEPLINKS;
};
